import React, { useState, useEffect } from "react"
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Container,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import CancelIcon from "@mui/icons-material/Cancel"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import { useParams, useNavigate } from "react-router-dom"
import * as documentsService from "../services/documentsService"
import * as customersService from "../services/adminService"
import { useCompanyId } from "../hooks/useCompanyId"
import { useTranslation } from "react-i18next"
import apiClient from "../services/apiClient"

interface LineItem {
    id: string
    itemName: string
    quantity: number
    unitPrice: number
    vatRateId: string
    vatRatePercent: number
    lineNet: number
    lineVat: number
    lineGross: number
}

const DEFAULT_VAT_RATE = 20

const createEmptyLine = (): LineItem => ({
    id: `line-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    itemName: "",
    quantity: 1,
    unitPrice: 0,
    vatRateId: "standard",
    vatRatePercent: DEFAULT_VAT_RATE,
    lineNet: 0,
    lineVat: 0,
    lineGross: 0,
})

const recalcLine = (line: LineItem): LineItem => {
    const lineNet = Math.round(line.quantity * line.unitPrice)
    const lineVat = Math.round(lineNet * (line.vatRatePercent / 100))
    return { ...line, lineNet, lineVat, lineGross: lineNet + lineVat }
}

const DocumentEditPage: React.FC = () => {
    const { id: documentId } = useParams<{ id: string }>()
    const { companyId } = useCompanyId()
    const navigate = useNavigate()
    const isNew = documentId === "new"
    const { t } = useTranslation()

    const [documentType, setDocumentType] = useState("invoice")
    const [currency, setCurrency] = useState("EUR")
    const [outletId, setOutletId] = useState("")
    const [outlets, setOutlets] = useState<any[]>([])
    const [customerId, setCustomerId] = useState("")
    const [customerSnapshot, setCustomerSnapshot] = useState<any>(null)
    const [customers, setCustomers] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [lineItems, setLineItems] = useState<LineItem[]>([createEmptyLine()])
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error" | "info"
    }>({
        open: false,
        message: "",
        severity: "success",
    })

    // Computed totals from line items
    const netTotal = lineItems.reduce((sum, l) => sum + l.lineNet, 0)
    const vatTotal = lineItems.reduce((sum, l) => sum + l.lineVat, 0)
    const grossTotal = netTotal + vatTotal

    useEffect(() => {
        if (!isNew && companyId && documentId) {
            loadDocument()
        }
        loadCustomers()
        loadOutlets()
    }, [companyId, documentId, isNew])

    const loadOutlets = async () => {
        if (!companyId) return
        try {
            const res = await apiClient.get(
                `/api/outlets?company_id=${companyId}`
            )
            const list = res.outlets || []
            setOutlets(list)
            if (!outletId && list.length > 0) {
                setOutletId(list[0].id)
            }
        } catch (err) {
            console.error("Failed to load outlets:", err)
        }
    }

    const loadDocument = async () => {
        if (!companyId || !documentId || isNew) return

        try {
            const { document: doc, lines } = await documentsService.getDocument(
                companyId,
                documentId
            )

            if (doc.status !== "validated") {
                setSnackbar({
                    open: true,
                    message: t("documentEdit.notifications.onlyValidated"),
                    severity: "info",
                })
                navigate(`/documents/${documentId}`, { replace: true })
                return
            }

            setDocumentType(doc.document_type)
            setCurrency(doc.currency)
            setCustomerId(doc.customer_id || "")
            setCustomerSnapshot(doc.customer_snapshot)

            // Reconstruct line items from sale lines if available, otherwise fallback to totals
            if (lines && lines.length > 0) {
                setLineItems(
                    lines.map((l: any, i: number) =>
                        recalcLine({
                            id: l.id || `line-${i}`,
                            itemName: l.label || l.item_name || "",
                            quantity: l.quantity || 1,
                            unitPrice: l.unit_price_minor ?? l.unit_price ?? 0,
                            vatRateId: l.vat_rate_id || "standard",
                            vatRatePercent:
                                l.vat_rate_percent ?? DEFAULT_VAT_RATE,
                            lineNet: 0,
                            lineVat: 0,
                            lineGross: 0,
                        })
                    )
                )
            } else if (doc.totals) {
                setLineItems([
                    {
                        id: "line-existing",
                        itemName: t("documentEdit.lines.existingTotal"),
                        quantity: 1,
                        unitPrice: doc.totals.net_total || 0,
                        vatRateId: "standard",
                        vatRatePercent:
                            doc.totals.net_total > 0
                                ? Math.round(
                                      (doc.totals.vat_total /
                                          doc.totals.net_total) *
                                          100
                                  )
                                : DEFAULT_VAT_RATE,
                        lineNet: doc.totals.net_total || 0,
                        lineVat: doc.totals.vat_total || 0,
                        lineGross: doc.totals.gross_total || 0,
                    },
                ])
            }
        } catch (error) {
            console.error("Failed to load document:", error)
        }
    }

    const loadCustomers = async () => {
        if (!companyId) return

        try {
            const result = await customersService.getCustomers(companyId, {
                limit: 100,
            })
            setCustomers(
                Array.isArray(result) ? result : result.customers || []
            )
        } catch (error) {
            console.error("Failed to load customers:", error)
        }
    }

    const handleCustomerChange = (_event: any, value: any) => {
        if (value) {
            setCustomerId(value.id)
            setCustomerSnapshot({
                name: value.name,
                company_name: value.company_name,
                email: value.email,
                phone: value.phone,
            })
        } else {
            setCustomerId("")
            setCustomerSnapshot(null)
        }
    }

    // Line item handlers
    const handleLineChange = (
        id: string,
        field: keyof LineItem,
        value: any
    ) => {
        setLineItems((prev) =>
            prev.map((line) => {
                if (line.id !== id) return line
                const updated = { ...line, [field]: value }
                return recalcLine(updated)
            })
        )
    }

    const handleAddLine = () => {
        setLineItems((prev) => [...prev, createEmptyLine()])
    }

    const handleRemoveLine = (id: string) => {
        setLineItems((prev) => {
            if (prev.length <= 1) return prev
            return prev.filter((l) => l.id !== id)
        })
    }

    // Build VAT breakdown from line items grouped by vatRateId
    const buildVatBreakdown = () => {
        const grouped: Record<
            string,
            { vat_rate_id: string; net_amount: number; vat_amount: number }
        > = {}
        for (const line of lineItems) {
            if (!grouped[line.vatRateId]) {
                grouped[line.vatRateId] = {
                    vat_rate_id: line.vatRateId,
                    net_amount: 0,
                    vat_amount: 0,
                }
            }
            grouped[line.vatRateId].net_amount += line.lineNet
            grouped[line.vatRateId].vat_amount += line.lineVat
        }
        return Object.values(grouped)
    }

    const handleSave = async () => {
        if (!companyId) return

        setLoading(true)
        try {
            const documentData = {
                outlet_id: outletId,
                document_type: documentType,
                customer_id: customerId || undefined,
                customer_snapshot: customerSnapshot,
                currency,
                totals: {
                    net_total: netTotal,
                    vat_total: vatTotal,
                    gross_total: grossTotal,
                },
                vat_breakdown: buildVatBreakdown(),
            }

            if (isNew) {
                const created = await documentsService.createDocument(
                    companyId,
                    documentData
                )
                setSnackbar({
                    open: true,
                    message: t("documentEdit.notifications.created"),
                    severity: "success",
                })
                navigate(`/documents/${created.id}`)
            } else if (documentId) {
                await documentsService.updateDocument(
                    companyId,
                    documentId,
                    documentData
                )
                setSnackbar({
                    open: true,
                    message: t("documentEdit.notifications.saved"),
                    severity: "success",
                })
                navigate(`/documents/${documentId}`)
            }
        } catch (error) {
            console.error("Failed to save document:", error)
            setSnackbar({
                open: true,
                message: t("documentEdit.notifications.saveError"),
                severity: "error",
            })
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        if (isNew) {
            navigate("/documents")
        } else {
            navigate(`/documents/${documentId}`)
        }
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4 }}>
                <Typography variant="h4" gutterBottom>
                    {isNew
                        ? t("documentEdit.title.new")
                        : t("documentEdit.title.edit")}
                </Typography>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {t("documentEdit.general.title")}
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>
                                    {t("documentEdit.general.documentType")}
                                </InputLabel>
                                <Select
                                    value={documentType}
                                    label={t(
                                        "documentEdit.general.documentType"
                                    )}
                                    onChange={(e) =>
                                        setDocumentType(e.target.value)
                                    }
                                    disabled={!isNew}
                                >
                                    <MenuItem value="receipt">
                                        {t(
                                            "documentEdit.documentTypes.receipt"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="invoice">
                                        {t(
                                            "documentEdit.documentTypes.invoice"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="credit_note">
                                        {t(
                                            "documentEdit.documentTypes.credit_note"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="quotation">
                                        {t(
                                            "documentEdit.documentTypes.quotation"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="sales_order">
                                        {t(
                                            "documentEdit.documentTypes.sales_order"
                                        )}
                                    </MenuItem>
                                    <MenuItem value="proforma">
                                        {t(
                                            "documentEdit.documentTypes.proforma"
                                        )}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>
                                    {t("documentEdit.general.outlet")}
                                </InputLabel>
                                <Select
                                    value={outletId}
                                    label={t("documentEdit.general.outlet")}
                                    onChange={(e) =>
                                        setOutletId(e.target.value)
                                    }
                                >
                                    {outlets.map((o) => (
                                        <MenuItem key={o.id} value={o.id}>
                                            {o.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>
                                    {t("documentEdit.general.currency")}
                                </InputLabel>
                                <Select
                                    value={currency}
                                    label={t("documentEdit.general.currency")}
                                    onChange={(e) =>
                                        setCurrency(e.target.value)
                                    }
                                >
                                    <MenuItem value="EUR">EUR</MenuItem>
                                    <MenuItem value="USD">USD</MenuItem>
                                    <MenuItem value="GBP">GBP</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Autocomplete
                                size="small"
                                options={customers}
                                getOptionLabel={(option) =>
                                    option.name ||
                                    option.company_name ||
                                    t("common.unknown")
                                }
                                value={
                                    customers.find(
                                        (c) => c.id === customerId
                                    ) || null
                                }
                                onChange={handleCustomerChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t(
                                            "documentEdit.general.customer"
                                        )}
                                    />
                                )}
                            />
                        </Grid>

                        {customerSnapshot && (
                            <Grid item xs={12}>
                                <Paper
                                    variant="outlined"
                                    sx={{ p: 1.5, bgcolor: "grey.50" }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {customerSnapshot.name ||
                                            customerSnapshot.company_name}
                                        {customerSnapshot.email &&
                                            ` · ${customerSnapshot.email}`}
                                        {customerSnapshot.phone &&
                                            ` · ${customerSnapshot.phone}`}
                                    </Typography>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* T021: Line Items Editor */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6">
                            {t("documentEdit.lines.title")}
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleAddLine}
                        >
                            {t("documentEdit.lines.add")}
                        </Button>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 200 }}>
                                        {t("documentEdit.lines.columns.item")}
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 90 }}>
                                        {t(
                                            "documentEdit.lines.columns.quantity"
                                        )}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ width: 120 }}
                                    >
                                        {t(
                                            "documentEdit.lines.columns.unitPrice"
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 90 }}>
                                        {t(
                                            "documentEdit.lines.columns.vatPercent"
                                        )}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ width: 110 }}
                                    >
                                        {t("documentEdit.lines.columns.net")}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ width: 110 }}
                                    >
                                        {t("documentEdit.lines.columns.vat")}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ width: 110 }}
                                    >
                                        {t("documentEdit.lines.columns.gross")}
                                    </TableCell>
                                    <TableCell sx={{ width: 50 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lineItems.map((line) => (
                                    <TableRow key={line.id}>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={line.itemName}
                                                onChange={(e) =>
                                                    handleLineChange(
                                                        line.id,
                                                        "itemName",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    t(
                                                        "documentEdit.lines.placeholders.item"
                                                    ) || "item"
                                                }
                                                variant="standard"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={line.quantity}
                                                onChange={(e) =>
                                                    handleLineChange(
                                                        line.id,
                                                        "quantity",
                                                        parseInt(
                                                            e.target.value
                                                        ) || 0
                                                    )
                                                }
                                                inputProps={{
                                                    min: 1,
                                                    style: {
                                                        textAlign: "right",
                                                    },
                                                }}
                                                variant="standard"
                                                sx={{ width: 70 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={line.unitPrice}
                                                onChange={(e) =>
                                                    handleLineChange(
                                                        line.id,
                                                        "unitPrice",
                                                        parseInt(
                                                            e.target.value
                                                        ) || 0
                                                    )
                                                }
                                                inputProps={{
                                                    min: 0,
                                                    style: {
                                                        textAlign: "right",
                                                    },
                                                }}
                                                variant="standard"
                                                sx={{ width: 100 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={line.vatRatePercent}
                                                onChange={(e) =>
                                                    handleLineChange(
                                                        line.id,
                                                        "vatRatePercent",
                                                        parseFloat(
                                                            e.target.value
                                                        ) || 0
                                                    )
                                                }
                                                inputProps={{
                                                    min: 0,
                                                    max: 100,
                                                    style: {
                                                        textAlign: "right",
                                                    },
                                                }}
                                                variant="standard"
                                                sx={{ width: 60 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {(line.lineNet / 100).toFixed(
                                                    2
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {(line.lineVat / 100).toFixed(
                                                    2
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                            >
                                                {(line.lineGross / 100).toFixed(
                                                    2
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip
                                                title={t(
                                                    "documentEdit.lines.remove"
                                                )}
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleRemoveLine(
                                                                line.id
                                                            )
                                                        }
                                                        disabled={
                                                            lineItems.length <=
                                                            1
                                                        }
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* T022: Computed Totals */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {t("documentEdit.totals.title")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                gap: 4,
                                width: 280,
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography variant="body1">
                                {t("documentEdit.totals.net")}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {(netTotal / 100).toFixed(2)} {currency}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 4,
                                width: 280,
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography variant="body1">
                                {t("documentEdit.totals.vat")}
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                                {(vatTotal / 100).toFixed(2)} {currency}
                            </Typography>
                        </Box>
                        <Divider sx={{ width: 280, my: 0.5 }} />
                        <Box
                            sx={{
                                display: "flex",
                                gap: 4,
                                width: 280,
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography variant="h6">
                                {t("documentEdit.totals.total")}
                            </Typography>
                            <Typography variant="h6" color="primary">
                                {(grossTotal / 100).toFixed(2)} {currency}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* Actions */}
                <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading
                            ? t("documentEdit.actions.saving")
                            : t("common.save")}
                    </Button>
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    )
}

export default DocumentEditPage
